export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    super(message);
  }
}

/**
 * Wird einmal pro Page-Load gezeigt, danach navigiert der Browser zum Auth-Service.
 * Vermeidet White-Flash, signalisiert die App-Wechsel-Aktion in Brand-Farben.
 */
let splashShown = false;
function triggerAuthSplash(targetUrl: string): void {
  if (splashShown) return;
  splashShown = true;
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;
                position:fixed;inset:0;background:#252E56;color:#fff;font-family:Poppins,sans-serif;z-index:99999">
      <div style="display:flex;align-items:center;justify-content:center;height:56px;width:56px;
                  border-radius:8px;background:rgba(255,255,255,0.10);box-shadow:inset 0 0 0 1px rgba(255,255,255,0.20)">
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#00CCCC" stroke-width="2.5">
          <path d="M4 4v16" stroke-linecap="round"/>
          <path d="M20 4L8 12l12 8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div style="margin-top:24px;font-size:11px;font-weight:300;text-transform:uppercase;
                  letter-spacing:0.08em;color:#00CCCC">kalkül</div>
      <div style="margin-top:4px;font-size:18px;font-weight:600">Inbox</div>
      <div style="margin-top:32px;font-size:12px;color:rgba(255,255,255,0.60)">Anmeldung wird vorbereitet…</div>
    </div>`;
  const root = document.createElement('div');
  root.innerHTML = html;
  document.body.appendChild(root);
  setTimeout(() => {
    window.location.href = targetUrl;
  }, 600);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(path, init);
  if (res.status === 401) {
    const returnUrl = encodeURIComponent(window.location.href);
    const target = `https://auth.kalkuel.app/auth/login?return_url=${returnUrl}`;
    triggerAuthSplash(target);
    throw new ApiError(401, 'unauthenticated');
  }
  if (!res.ok) {
    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      payload = await res.text();
    }
    throw new ApiError(res.status, `${method} ${path} -> ${res.status}`, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Multipart-Upload (kein JSON content-type — der Browser setzt die
 * multipart-Boundary selbst). Spiegelt das Fehler-/401-Verhalten von request().
 */
async function upload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  if (res.status === 401) {
    const returnUrl = encodeURIComponent(window.location.href);
    triggerAuthSplash(`https://auth.kalkuel.app/auth/login?return_url=${returnUrl}`);
    throw new ApiError(401, 'unauthenticated');
  }
  if (!res.ok) {
    let payload: unknown;
    try {
      payload = await res.json();
    } catch {
      payload = await res.text();
    }
    throw new ApiError(res.status, `POST ${path} -> ${res.status}`, payload);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Identität des aktuellen Nutzers vom zentralen Auth-Service.
 * `sub` entspricht der inbox-User-ID (siehe API-Auth-Sync) — damit lassen sich
 * eigene Reaktionen erkennen.
 */
async function fetchMe(): Promise<import('../types').AuthMe> {
  const res = await fetch('https://auth.kalkuel.app/auth/me', { credentials: 'include' });
  if (!res.ok) throw new ApiError(res.status, 'auth_me_failed');
  return (await res.json()) as import('../types').AuthMe;
}

/** Öffentlich abrufbare URL eines Medien-Assets (für <img>, <video>, Download). */
export function mediaUrl(assetId: string): string {
  return `/api/media/${assetId}`;
}

/**
 * Lädt eine Datei vom Server herunter (Blob → temporärer Download-Link).
 * Nutzt `credentials: include`, damit der Auth-Cookie mitgeht — anders als ein
 * reines `<a download href>` (das ginge ohne Cookie-Garantie und ohne 401-Handling).
 */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(path, { credentials: 'include' });
  if (res.status === 401) {
    const returnUrl = encodeURIComponent(window.location.href);
    triggerAuthSplash(`https://auth.kalkuel.app/auth/login?return_url=${returnUrl}`);
    throw new ApiError(401, 'unauthenticated');
  }
  if (!res.ok) throw new ApiError(res.status, `GET ${path} -> ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  upload,
  download,
  me: fetchMe,
};
